import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  Activity,
  Menu,
  X,
  LogOut,
  User,
  Network,
  Boxes,
  Database,
  Map,
  Pin,
  PinOff,
  Code2,
  Workflow,
  Target,
  Cable,
  MessageCircle,
  Home,
  UsersRound,
  GitBranch,
  LayoutPanelTop,
  ChevronsUpDown,
  FlaskConical,
  Mail,
} from 'lucide-react';
import { canAccessAdminPage, getAdminShellPathForPage, isPlatformConfigurator } from '../../router/routeManifest';
import SidebarNavItem from './SidebarNavItem';
import SidebarMoreMenu from './SidebarMoreMenu';
import { getSidebarNavIconColorClass } from './sidebarNavStyles';
import { loadSidebarMounted, saveSidebarMounted } from './sidebarMountPreference';
import { loadPinnedPages, savePinnedPages } from './sidebarPinnedPages';
import { CONFIG_PAGES, navItemForPage } from '../navSections';

const RAIL_WIDTH = 'w-[3.25rem]';
const EXPANDED_WIDTH_CLASS = 'w-[12.5rem]';
const EXPANDED_HOVER_WIDTH = 'hover:w-[12.5rem]';

/** How each configuration page appears in More and, once pinned, in the sidebar. */
const CONFIG_ITEMS = {
  'view-editor': { label: 'App Editor', Icon: LayoutPanelTop, activeColor: 'violet' },
  'ontology-builder': { label: 'Ontology', Icon: Boxes, activeColor: 'violet' },
  'ontology-data': { label: 'Records', Icon: Database, activeColor: 'teal' },
  integrations: { label: 'Integrations', Icon: Network, activeColor: 'indigo' },
  team: { label: 'Agents', Icon: UsersRound, activeColor: 'violet' },
  'org-chart': { label: 'Org Chart', Icon: GitBranch, activeColor: 'cyan' },
  initiatives: { label: 'Initiatives', Icon: Target, activeColor: 'rose' },
  automations: { label: 'Automations', Icon: Workflow, activeColor: 'indigo' },
  build: { label: 'Process Map', Icon: Map, activeColor: 'cyan' },
  'program-editor': { label: 'Programs', Icon: Code2, activeColor: 'sky' },
  'ontology-action-runs': { label: 'Action history', Icon: Activity, activeColor: 'green' },
  simulations: { label: 'Simulations', Icon: FlaskConical, activeColor: 'amber' },
};

/** Account, help and personal connections, kept out of the daily list. */
function AccountMenu({ items, showLabels, inline, onSelect }) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ left: 16, bottom: 16 });
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const bounds = triggerRef.current?.getBoundingClientRect();
    // Opens upward from the trigger, so it stays anchored when a hovered rail collapses.
    if (bounds) setPlacement({ left: Math.max(8, bounds.left), bottom: Math.max(8, window.innerHeight - bounds.top + 6) });
    const dismiss = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const choose = (item) => {
    setOpen(false);
    onSelect(item);
  };

  if (inline) {
    return items.map((item) => (
      <SidebarNavItem
        key={item.id}
        icon={<item.Icon className={item.danger ? 'text-slate-400' : getSidebarNavIconColorClass('blue', false)} strokeWidth={1.75} aria-hidden />}
        label={item.label}
        showLabels
        variant={item.danger ? 'danger' : 'default'}
        onClick={() => choose(item)}
      />
    ));
  }

  return (
    <div ref={triggerRef}>
      <SidebarNavItem
        icon={<User className={getSidebarNavIconColorClass('blue', open)} strokeWidth={1.75} aria-hidden />}
        label="Account"
        showLabels={showLabels}
        isActive={open}
        trailing={<ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden />}
        onClick={() => setOpen((value) => !value)}
        ariaHasPopup="menu"
        ariaExpanded={open}
      />
      {open ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Account"
          style={placement}
          className="fixed z-[60] w-56 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 text-sm shadow-xl ring-1 ring-black/5"
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              onClick={() => choose(item)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${item.danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <item.Icon className="h-4 w-4 shrink-0 opacity-70" strokeWidth={1.75} aria-hidden />
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

/**
 * Everyone's sidebar is Home and their published apps. Admins open More, under
 * the last app, for configuration pages, and can pin any of those pages here.
 */
export function AppSidebarView({
  activePage,
  setActivePage,
  onNavigatePath,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onSignOut,
  userRole,
  enabledApplicationModules = [],
  hasPageChanges = () => false,
  needsYouCount = 0,
  // The product puts Home first. The public demo turns this off and hides Agents.
  showTeamNavigation = true,
  // The public demo keeps its standalone Initiatives screen; the product reaches
  // initiatives through their agents (Agents, agent profiles, Home, Needs you).
  showInitiativesNavigation = false,
  renderViewsNavigation = null,
}) {
  const location = useLocation();
  const [isMounted, setIsMounted] = useState(() => loadSidebarMounted());
  const [pinnedPages, setPinnedPages] = useState(() => loadPinnedPages());
  const [moreOpen, setMoreOpen] = useState(false);
  const isConfigurator = isPlatformConfigurator(userRole);
  const toggleMounted = () => {
    setIsMounted((current) => {
      const next = !current;
      saveSidebarMounted(next);
      return next;
    });
  };
  const togglePin = (page) => {
    setPinnedPages((current) => {
      const next = current.includes(page) ? current.filter((id) => id !== page) : [...current, page];
      savePinnedPages(next);
      return next;
    });
  };
  const canAccess = (page) => canAccessAdminPage(userRole, page, { accessibleModules: enabledApplicationModules });

  const configItems = useMemo(
    () =>
      isConfigurator
        ? CONFIG_PAGES.filter((page) => {
            if (page === 'team' || page === 'org-chart') return showTeamNavigation && canAccess(page);
            if (page === 'initiatives') return showInitiativesNavigation && canAccess(page);
            return canAccess(page);
          }).map((page) => ({ page, ...CONFIG_ITEMS[page], hasChanges: hasPageChanges(page) }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isConfigurator, showTeamNavigation, showInitiativesNavigation, userRole, enabledApplicationModules, hasPageChanges]
  );
  const pinnedItems = configItems.filter((item) => pinnedPages.includes(item.page));

  const activeNavItem = showTeamNavigation ? navItemForPage(activePage, location.pathname) : activePage;
  // The menu must not switch the rail into its pinned layout. That resize changes
  // every row's padding, the dots jump off the pointer, and the sidebar fidgets.

  const go = (path) => {
    onNavigatePath?.(path);
    setIsMobileMenuOpen(false);
  };

  const renderNavItem = ({ page, label, Icon, activeColor, path = null, badge = null, onClick = null, hasChanges = null }, showLabels) => {
    const resolvedPath = path || (page ? getAdminShellPathForPage(page) : null);
    const isActive = Boolean(page) && activeNavItem === page;
    return (
      <SidebarNavItem
        key={page || resolvedPath || label}
        icon={
          <Icon
            className={getSidebarNavIconColorClass(activeColor, isActive)}
            strokeWidth={isActive ? 2.25 : 1.75}
            aria-hidden
          />
        }
        label={label}
        badge={badge}
        hasChanges={hasChanges ?? hasPageChanges(page)}
        activeColor={activeColor}
        showLabels={showLabels}
        isActive={isActive}
        onClick={() => {
          if (onClick) {
            onClick();
            setIsMobileMenuOpen(false);
          } else if (resolvedPath) {
            go(resolvedPath);
          } else if (page) {
            setActivePage(page);
            setIsMobileMenuOpen(false);
          }
        }}
      />
    );
  };

  const renderPinControl = (showLabels) => {
    const PinIcon = isMounted ? PinOff : Pin;

    return (
      <div className="shrink-0 border-b border-slate-200/70 px-1.5 py-2">
        <button
          type="button"
          onClick={toggleMounted}
          aria-pressed={isMounted}
          aria-label={isMounted ? 'Unpin sidebar' : 'Pin sidebar open'}
          title={isMounted ? 'Unpin sidebar' : 'Pin sidebar open'}
          className={`group/pin relative flex w-full items-center rounded-lg text-slate-500 transition-[background-color,color,padding] duration-200 ease-out hover:bg-slate-900/[0.04] hover:text-slate-700 ${
            showLabels ? 'justify-start px-2.5 py-2' : 'justify-start px-2.5 py-2.5'
          } ${isMounted ? 'bg-slate-900/[0.04] text-slate-700' : ''}`}
        >
          <PinIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
          <span
            className={`overflow-hidden whitespace-nowrap text-[13px] font-medium tracking-[-0.01em] transition-[max-width,opacity,margin] duration-200 ease-out ${
              showLabels
                ? 'ml-2.5 max-w-[11rem] opacity-100'
                : 'max-w-0 opacity-0 group-hover/sidebar:ml-2.5 group-hover/sidebar:max-w-[11rem] group-hover/sidebar:opacity-100 group-data-[open=true]/sidebar:ml-2.5 group-data-[open=true]/sidebar:max-w-[11rem] group-data-[open=true]/sidebar:opacity-100'
            }`}
          >
            {isMounted ? 'Unpin sidebar' : 'Pin sidebar'}
          </span>
        </button>
      </div>
    );
  };

  const renderPrimary = (showLabels, inline) => (
    <>
      {showTeamNavigation
        ? renderNavItem({ page: 'home', label: 'Home', Icon: Home, activeColor: 'cyan', badge: needsYouCount }, showLabels)
        : null}
      {canAccess('views')
        ? renderViewsNavigation?.({ showLabels, activePath: location.pathname, onNavigate: go })
        : null}
      {pinnedItems.map((item) => renderNavItem(item, showLabels))}
      {configItems.length > 0 ? (
        <SidebarMoreMenu
          items={configItems}
          pinnedPages={pinnedPages}
          activePage={activeNavItem}
          showLabels={showLabels}
          inline={inline}
          onTogglePin={togglePin}
          onSelect={(page) => go(getAdminShellPathForPage(page))}
          onOpenChange={setMoreOpen}
        />
      ) : null}
    </>
  );

  const accountItems = [
    canAccess('mcp') && { id: 'mcp', label: 'AI connections', Icon: Cable, page: 'mcp' },
    canAccess('senders') && { id: 'senders', label: 'Mailbox access', Icon: Mail, page: 'senders' },
    canAccess('contact') && { id: 'contact', label: 'Help & contact', Icon: MessageCircle, page: 'contact' },
    canAccess('account') && { id: 'account', label: 'Account settings', Icon: User, page: 'account' },
    { id: 'sign-out', label: 'Sign out', Icon: LogOut, danger: true },
  ].filter(Boolean);
  const selectAccountItem = (item) => {
    if (item.id === 'sign-out') onSignOut();
    else setActivePage(item.page);
    setIsMobileMenuOpen(false);
  };

  const renderNavContent = (showLabels = false, inline = false) => (
    <div className="flex h-full flex-col font-sidebar antialiased">
      {renderPinControl(showLabels)}
      <nav className="flex flex-grow flex-col overflow-x-hidden overflow-y-auto px-1.5 py-3" aria-label="Your apps">
        <div className="space-y-0.5">{renderPrimary(showLabels, inline)}</div>
        <div className="mt-auto shrink-0 space-y-0.5 border-t border-slate-200/70 pt-2">
          <AccountMenu items={accountItems} showLabels={showLabels} inline={inline} onSelect={selectAccountItem} />
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <button
        type="button"
        className="fixed right-4 top-4 z-50 rounded-lg border border-slate-200/80 bg-white/95 p-2 text-slate-600 shadow-sm backdrop-blur-sm md:hidden"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      <div
        inert={isMobileMenuOpen ? undefined : ""}
        aria-hidden={!isMobileMenuOpen}
        className={`fixed inset-0 z-40 transform bg-white transition-transform duration-300 ease-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full overflow-hidden pt-16">{renderNavContent(true, true)}</div>
      </div>

      <div
        className={`hidden shrink-0 transition-[width] duration-200 ease-out md:block ${
          isMounted ? EXPANDED_WIDTH_CLASS : RAIL_WIDTH
        }`}
        aria-hidden="true"
      />

      <aside
        className={`group/sidebar fixed inset-y-0 left-0 z-40 hidden flex-col overflow-hidden border-r border-slate-200/80 bg-slate-50/95 backdrop-blur-md transition-[width,box-shadow] duration-200 ease-out md:flex ${
          isMounted
            ? `${EXPANDED_WIDTH_CLASS} shadow-[1px_0_0_0_rgba(15,23,42,0.04)]`
            : `${RAIL_WIDTH} ${EXPANDED_HOVER_WIDTH} shadow-[1px_0_0_0_rgba(15,23,42,0.04)] hover:shadow-[4px_0_24px_-6px_rgba(15,23,42,0.1)]`
        }`}
        // The More menu sits outside the rail: while it is open the rail stays open, labels and all.
        data-open={moreOpen && !isMounted ? 'true' : undefined}
        style={moreOpen && !isMounted ? { width: '12.5rem', transitionProperty: 'none' } : undefined}
      >
        {renderNavContent(isMounted)}
      </aside>

      {isMobileMenuOpen ? (
        <div
          role="presentation"
          className="fixed inset-0 z-30 bg-slate-900/20 backdrop-blur-[1px] md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      ) : null}
    </>
  );
}
